<template>
  <v-dialog v-model="hModalData.dialog" max-width="60%" persistent scrollable>
    <v-card>
      <v-card-title class="relative">
        Vehicle History
        <v-spacer></v-spacer>
        <v-btn icon class="close-icon absolute right-0 top-0" @click="closeHistoryModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="historyForm" v-model="formValid" lazy-validation>
          <v-container>
            <div class="text-caption mb-3">
              <span class="error--text font-weight-bold">*</span> Required fields
            </div>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formHistoryModel.title"
                  :rules="requiredRules"
                  label="Title *"
                  required
                />
              </v-col>

              <v-col v-if="usesOdometer" cols="12" md="6">
                <v-text-field
                  v-model="formHistoryModel.odometer_km"
                  :rules="requiredRules.concat(odometerKmRules)"
                  label="Current km *"
                  type="number"
                  required
                />
              </v-col>

              <v-col cols="12" :md="usesOdometer ? 6 : 12">
                <v-select
                  v-model="formHistoryModel.type"
                  :items="availableHistoryTypes"
                  label="Type *"
                  item-text="text"
                  item-value="value"
                  :rules="serviceTypeRules"
                  menu-props="auto"
                  single-line
                  prepend-icon="mdi-wrench"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formHistoryModel.cost_net"
                  label="Cost net"
                  type="number"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label for="occurredat-full-width">Occurred at <span class="error--text font-weight-bold">*</span></label>
                <b-form-datepicker
                  id="occurredat-full-width"
                  v-model="formHistoryModel.occurred_at"
                  :min="minDate"
                  calendar-width="100%"
                  locale="en"
                  start-weekday="1"
                  class="mb-2"
                  style="max-width: 260px"
                  required
                  @context="messages.occurred_at = ''"
                />
                <small v-if="messages.occurred_at" style="color: red">
                  {{ messages.occurred_at }}
                </small>
              </v-col>

              <v-col cols="12" md="6">
                <v-textarea
                  v-model="formHistoryModel.description"
                  label="Description"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  :key="historySelectKey"
                  v-model="historySelectMeta"
                  :items="historyMeta"
                  label="Additional data / document"
                  item-text="text"
                  item-value="value"
                  menu-props="auto"
                  single-line
                  clearable
                  prepend-icon="mdi-paperclip"
                />
              </v-col>

              <v-col
                v-for="key in metaOrder"
                :key="key"
                cols="12"
                :md="key === 'parts' ? 12 : 6"
              >
                <div class="mb-3">
                  <template v-if="isDocumentMetaKey(key)">
                    <div class="d-flex align-baseline gap-3">
                      <v-file-input
                        v-model="formHistoryModel.files"
                        :label="`${metaLabels[key] || key}: choose file(s)...`"
                        dense
                        outlined
                        show-size
                        multiple
                        :rules="[filesRule]"
                      >
                        <template v-slot:selection="{ index, text }">
                          <v-chip
                            v-if="index < 2"
                            color="deep-purple accent-4"
                            dark
                            label
                            small
                          >
                            {{ text }}
                          </v-chip>
                          <span
                            v-else-if="index === 2"
                            class="text-overline grey--text text--darken-3 mx-2"
                          >
                            +{{ formHistoryModel.files.length - 2 }} file(s)
                          </span>
                        </template>
                      </v-file-input>

                      <v-btn icon @click="removeMeta(key)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </template>

                  <template v-else-if="key === 'parts'">
                    <v-card outlined class="pa-3">
                      <div class="d-flex align-center mb-2">
                        <div class="subtitle-2">{{ metaLabels.parts || 'Parts' }}</div>
                        <v-spacer></v-spacer>
                        <v-btn small @click="addPart">+ Add part</v-btn>
                        <v-btn icon @click="removeMeta('parts')">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </div>

                      <div
                        v-for="(part, index) in formHistoryModel.meta.parts || []"
                        :key="index"
                        class="d-flex align-center"
                      >
                        <v-text-field v-model="part.name" label="Name" class="mr-2" />
                        <v-text-field v-model.number="part.qty" type="number" label="Qty" class="mr-2" />
                        <v-text-field v-model.number="part.price_net" type="number" label="Net price" class="mr-2" />
                        <v-btn icon @click="removePart(index)">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </v-card>
                  </template>

                  <template v-else>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model="formHistoryModel.meta[key]"
                        :label="metaLabels[key] || key"
                        class="flex-grow-1"
                      />
                      <v-btn icon @click="removeMeta(key)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </div>
              </v-col>

              <v-col v-if="formHistoryModel.id && currentHistoryAttachments.length" cols="12">
                <div class="text-subtitle-1 font-weight-bold mb-2">Documents</div>
                <v-list dense>
                  <v-list-item v-for="attachment in currentHistoryAttachments" :key="attachment.id">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ attachmentDisplayName(attachment) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <v-img
                          lazy-src="public/placeholder/placeholder_img.jpg"
                          max-height="150"
                          max-width="250"
                          :src="attachment.path ? getFile(attachment) : ''"
                          contain
                          class="image-hover"
                          @click="openPublicFile(attachment)"
                        />
                        {{ attachment.mime_type }} • {{ attachmentSize(attachment) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-btn icon @click="handleFileRemove(formHistoryModel, attachment)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" class="d-flex justify-end flex-wrap">
                <v-btn color="error" class="mr-2" @click.stop="closeHistoryModal">Cancel</v-btn>
                <v-btn color="primary" @click.stop="handleAddHistory">
                  {{ hModalData.data ? 'Update History' : 'Add History' }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </v-card>

    <v-overlay :value="loader">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </v-dialog>
</template>

<style>
.close-icon {
  top: 5px;
  right: 5px;
  transition: 0.5s all ease;
}
.close-icon:hover {
  color: red !important;
}
.image-hover:hover {
  cursor: pointer;
  opacity: 0.8;
}
</style>

<script>
import ApiService from "@/core/services/api.service";
import { formModelMixins } from "@/view/mixins/formModelMixins.js";
import Swal from "sweetalert2";

const HISTORY_ENDPOINT = "transport/vehicleHistory";
const VEHICLE_ENDPOINT = "transport/vehicle";

const DOCUMENT_META_KEYS = [
  "invoice_no",
  "document",
  "other_document",
  "decision_document",
  "insurance_document",
  "maintenance_document",
];

const initialHistoryData = () => ({
  id: null,
  vehicle_id: null,
  occurred_at: null,
  odometer_km: null,
  type: null,
  title: "",
  description: "",
  cost_net: 0,
  currency: null,
  meta: {},
  created_by: null,
  files: [],
  attachments: [],
});

export default {
  name: "VehicleHistoryModal",
  mixins: [formModelMixins],
  props: {
    hModalData: { type: Object, required: true },
    permissions: { type: Array, default: () => [] },
    historyattachments: { type: Array, default: () => [] },
    assetType: { type: String, default: "vehicle" },
    openPublicFile: { type: Function, required: true },
    getFile: { type: Function, required: true },
  },
  data() {
    return {
      loader: false,
      formValid: false,
      formHistoryModel: initialHistoryData(),
      historySelectMeta: null,
      historySelectKey: 0,
      metaOrder: [],
      messages: {},
      allowedExt: ["pdf", "png", "jpg", "jpeg", "webp"],
      historyMeta: [
        { value: "invoice_no", text: "Invoice" },
        { value: "document", text: "Document" },
        { value: "other_document", text: "Other document" },
        { value: "decision_document", text: "Decision / authority document" },
        { value: "insurance_document", text: "Insurance document" },
        { value: "maintenance_document", text: "Maintenance document" },
        { value: "workshop", text: "Workshop" },
        { value: "note", text: "Note" },
        { value: "parts", text: "Parts" },
      ],
      historyTypes: [
        { value: "activity", text: "Activity / mileage" },
        { value: "service", text: "General service" },
        { value: "oil_change", text: "Oil change" },
        { value: "timing_belt", text: "Timing belt" },
        { value: "repair", text: "Repair" },
        { value: "tire", text: "Tire" },
        { value: "document", text: "Document" },
        { value: "other", text: "Other" },
      ],
      metaLabels: {
        invoice_no: "Invoice",
        document: "Document",
        other_document: "Other document",
        decision_document: "Decision / authority document",
        insurance_document: "Insurance document",
        maintenance_document: "Maintenance document",
        workshop: "Workshop",
        note: "Note",
        parts: "Parts",
      },
      requiredRules: [(value) => !!value || "This field is required"],
      odometerKmRules: [
        (value) => value === null
          || value === ""
          || (!isNaN(Number(value)) && Number(value) >= 0)
          || "Odometer must be a non-negative number",
      ],
      serviceTypeRules: [(value) => !!value || "Type is required"],
    };
  },
  computed: {
    usesOdometer() {
      return this.assetType !== "drone";
    },
    availableHistoryTypes() {
      if (this.usesOdometer) return this.historyTypes;
      return this.historyTypes.filter((item) => ["service", "repair", "document", "other"].includes(item.value));
    },
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 20);
      return date.toISOString().slice(0, 10);
    },
    selectedDocumentMetaKey() {
      return this.metaOrder.find((key) => this.isDocumentMetaKey(key)) || null;
    },
    currentHistoryAttachments() {
      if (!this.formHistoryModel.id) return [];

      const direct = Array.isArray(this.formHistoryModel.attachments)
        ? this.formHistoryModel.attachments
        : [];
      const fromVehicle = Array.isArray(this.historyattachments)
        ? this.historyattachments.filter((attachment) => {
            const historyId = attachment.vhistory_id !== undefined
              ? attachment.vhistory_id
              : attachment.history_id;
            return Number(historyId) === Number(this.formHistoryModel.id);
          })
        : [];

      const map = new Map();
      [...direct, ...fromVehicle].forEach((attachment) => map.set(attachment.id, attachment));
      return Array.from(map.values());
    },
  },
  watch: {
    "hModalData.dialog"(open) {
      if (open) this.prefillHistoryForm();
    },
    historySelectMeta(value) {
      if (!value) return;

      if (value === "parts") {
        if (!Array.isArray(this.formHistoryModel.meta.parts)) {
          this.$set(this.formHistoryModel.meta, "parts", [this.defaultPart()]);
        }
        if (!this.metaOrder.includes("parts")) this.metaOrder.push("parts");
        this.resetMetaSelect();
        return;
      }

      if (this.isDocumentMetaKey(value)) {
        DOCUMENT_META_KEYS.forEach((key) => {
          if (key !== value && Object.prototype.hasOwnProperty.call(this.formHistoryModel.meta, key)) {
            this.$delete(this.formHistoryModel.meta, key);
          }
        });
        this.metaOrder = this.metaOrder.filter((key) => !this.isDocumentMetaKey(key));
        this.$set(this.formHistoryModel.meta, value, true);
        this.metaOrder.push(value);
        this.resetMetaSelect();
        return;
      }

      if (!Object.prototype.hasOwnProperty.call(this.formHistoryModel.meta, value)) {
        this.$set(this.formHistoryModel.meta, value, "");
      }
      if (!this.metaOrder.includes(value)) this.metaOrder.push(value);
      this.resetMetaSelect();
    },
  },
  methods: {
    isDocumentMetaKey(key) {
      return DOCUMENT_META_KEYS.includes(key);
    },
    resetMetaSelect() {
      this.historySelectMeta = null;
      this.historySelectKey += 1;
    },
    attachmentDisplayName(attachment) {
      if (!attachment) return "Document";
      if (attachment.original_name) return attachment.original_name;
      if (attachment.filename) return attachment.filename;

      const path = String(attachment.path || "").split("?")[0];
      const filename = path.split("/").pop();
      return filename || attachment.mime_type || "Document";
    },
    attachmentSize(attachment) {
      const size = Number(attachment && attachment.size);
      return Number.isFinite(size) ? `${(size / 1024).toFixed(1)} KB` : "Unknown size";
    },
    filesRule(files) {
      if (!files) return true;
      const selected = Array.isArray(files) ? files : [files];
      const invalid = selected.some((file) => {
        const extension = String(file.name || "").split(".").pop().toLowerCase();
        return !this.allowedExt.includes(extension);
      });
      return invalid ? "You can upload only jpg, jpeg, png, pdf, webp files" : true;
    },
    removeMeta(key) {
      this.$delete(this.formHistoryModel.meta, key);
      this.metaOrder = this.metaOrder.filter((item) => item !== key);
      if (this.isDocumentMetaKey(key)) this.formHistoryModel.files = [];
    },
    handleFileRemove(model, file) {
      Swal.fire({
        title: "Are you sure you want to delete this file?",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#00ad00",
        cancelButtonColor: "#e33354",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.loader = true;
        ApiService.delete(`${HISTORY_ENDPOINT}/${model.id}/attachments/${file.id}`)
          .then((response) => {
            const attachments = response && response.data && response.data.attachments
              ? response.data.attachments
              : [];
            this.formHistoryModel.attachments = attachments;
            this.$emit("history-attachment-deleted", {
              attachmentId: file.id,
              historyId: model.id,
              attachments,
            });
            Swal.fire("Deleted!", "", "success");
          })
          .catch(() => Swal.fire("Something went wrong!", "", "error"))
          .finally(() => { this.loader = false; });
      });
    },
    handleAddHistory() {
      const valid = this.$refs.historyForm && this.$refs.historyForm.validate
        ? this.$refs.historyForm.validate()
        : true;
      if (!valid) return Promise.resolve(false);

      if (!this.formHistoryModel.occurred_at) {
        this.$set(this.messages, "occurred_at", "This field is required");
        return Promise.resolve(false);
      }

      const model = { ...this.formHistoryModel };
      model.vehicle_id = model.vehicle_id || this.hModalData.editedId;

      const formData = new FormData();
      Object.keys(model).forEach((key) => {
        if (["meta", "files", "attachments"].includes(key)) return;
        if (model[key] !== null && model[key] !== undefined && model[key] !== "") {
          formData.append(key, model[key]);
        }
      });

      formData.append("meta", JSON.stringify(this.formHistoryModel.meta || {}));
      if (this.selectedDocumentMetaKey) {
        formData.append("attachment_category", this.selectedDocumentMetaKey);
      }

      (this.formHistoryModel.files || []).forEach((file) => {
        formData.append("files[]", file, file.name);
      });

      const isUpdate = !!model.id;
      if (isUpdate) formData.append("_method", "PUT");

      this.loader = true;
      const request = isUpdate
        ? ApiService.post(`${HISTORY_ENDPOINT}/${model.id}`, formData)
        : ApiService.post(`${VEHICLE_ENDPOINT}/${model.vehicle_id}/history`, formData);

      return request
        .then((response) => {
          this.$emit("history-saved", {
            mode: isUpdate ? "update" : "create",
            item: response && response.data ? response.data : response,
          });
          this.closeHistoryModal();
          return response;
        })
        .catch((error) => {
          const errors = error && error.response ? error.response.data : null;
          if (errors) {
            Object.keys(errors).forEach((field) => {
              const message = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
              this.setError(field, message);
            });
          }
          return false;
        })
        .finally(() => { this.loader = false; });
    },
    closeHistoryModal() {
      this.hModalData.dialog = false;
      this.$nextTick(() => setTimeout(() => this.resetHistoryForm(), 250));
    },
    resetHistoryForm() {
      this.formHistoryModel = initialHistoryData();
      this.formValid = false;
      this.loader = false;
      this.metaOrder = [];
      this.messages = {};
      this.resetMetaSelect();
      this.hModalData.data = null;
      this.hModalData.editedId = null;
      if (this.$refs.historyForm && this.$refs.historyForm.resetValidation) {
        this.$refs.historyForm.resetValidation();
      }
    },
    prefillHistoryForm() {
      this.resetMetaSelect();
      const history = this.hModalData.data;

      if (!history) {
        this.formHistoryModel = initialHistoryData();
        this.metaOrder = [];
        return;
      }

      const cloned = JSON.parse(JSON.stringify(history));
      let meta = cloned.meta || {};
      if (typeof meta === "string") {
        try { meta = JSON.parse(meta) || {}; } catch (error) { meta = {}; }
      }

      this.formHistoryModel = {
        ...initialHistoryData(),
        ...cloned,
        occurred_at: cloned.occurred_at ? String(cloned.occurred_at).substring(0, 10) : null,
        meta,
        files: [],
        attachments: Array.isArray(cloned.attachments) ? cloned.attachments : [],
      };
      this.metaOrder = Object.keys(meta);
    },
    defaultPart() {
      return { name: "", qty: 1, price_net: null };
    },
    addPart() {
      if (!Array.isArray(this.formHistoryModel.meta.parts)) {
        this.$set(this.formHistoryModel.meta, "parts", []);
      }
      this.formHistoryModel.meta.parts.push(this.defaultPart());
    },
    removePart(index) {
      if (!Array.isArray(this.formHistoryModel.meta.parts)) return;
      this.formHistoryModel.meta.parts.splice(index, 1);
      if (this.formHistoryModel.meta.parts.length === 0) {
        this.formHistoryModel.meta.parts.push(this.defaultPart());
      }
    },
  },
};
</script>
